import * as React from 'react';
import FileUploadIcon from '@mui/icons-material/FileUpload';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import Stack from '@mui/material/Stack';
import Box from '@mui/material/Box';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { EditedImagesApi } from '../queries/EditedImagesApi';
import Button from '@mui/material/Button';
import { shouldDisableDelivery } from '../state/deliveryUtils';
import { ImageDropzone, ImageDropzoneLabels } from '../../../shared/view/ImageDropzone';
import { ImageModel, ImageUploadResponse } from '../../../shared/types';
import { useImageDataUpdate } from '../../../shared/state/useImageDataUpdate';
import {
    DigitalStylingThumbnails,
    DigitalStylingThumbnailsLabels
} from '../../../shared/view/DigitalStylingThumbnails';
import { UploadsInProgress } from '../../../shared/view/UploadsInProgress';
import { FailedUploads } from '../../../shared/view/FailedUploads';
import { hasUnsettledImages, IMAGE_REFETCH_INTERVAL } from '../../../shared/state/thumbnailUtils';
import { downloadZip } from '../../../shared/state/downloadUtils';
import { useApiUrl } from '../../../../../../../shared/hooks/useApiUrl';
import { CustomerOrSupportImage } from '../../uneditedImages/view/UploadUneditedImages';
import { DigitalStylingOrderListQueryKey } from '../../../../../list/digitalStyling/entry/view/DigitalStylingOrderListPage';

export type UploadEditedImagesLabels = {
    uploadButton: string;
    failedUploadsHeading: string;
    uploadsInProgressHeading: string;
    deliverImagesButton: string;
} & ImageDropzoneLabels &
    DigitalStylingThumbnailsLabels;

type UploadEditedImagesProps = {
    onUpload: (formData: FormData) => Promise<number[]>;
    imageData: ImageModel[];
    onDeleteFailedUpload: (photoUploadMetadataId: number) => void;
    setImageInProgress: (imageUploadResponse: ImageUploadResponse) => void;
    onDeleteSingleImage: (imageId: number) => Promise<void>;
    onDeleteMultipleImages: (images: CustomerOrSupportImage[]) => void;
    onDownloadImages: () => Promise<any>;
    onUploadComplete: () => void;
    onRetryUpload: () => void;
    onDeliverImages: () => void;
    orderStatus: {
        displayName: string;
        value: number;
    };
} & ExternalProps;

export type ExternalProps = {
    labels: UploadEditedImagesLabels;
    orderId: number;
};

export function UploadEditedImages({
    orderId,
    labels,
    onRetryUpload,
    imageData,
    onDeleteFailedUpload,
    onDeleteMultipleImages,
    onDeleteSingleImage,
    onDownloadImages,
    onUploadComplete,
    onUpload,
    orderStatus,
    onDeliverImages,
    setImageInProgress
}: UploadEditedImagesProps) {
    let formId = `edited-${orderId}`;
    let baseUrl = window.appConfig.apiUrl;
    let formAction = `${baseUrl}/views/digital-styling-upload?orderId=${orderId}`;

    let {
        filesAdded,
        addFileToDropzone,
        removeFileFromDropzone,
        isDoneUploading,
        setIsDoneUploading
    } = useImageDataUpdate(imageData, onUploadComplete);

    function handleUpload(formData: FormData): Promise<number[]> {
        setIsDoneUploading(false);
        setDisableDeliveryButton(false);
        return onUpload(formData);
    }

    let [disableDeliveryButton, setDisableDeliveryButton] = React.useState(false);

    function handleDeliverImages() {
        setDisableDeliveryButton(true);
        onDeliverImages();
    }

    return (
        <>
            {imageData.map((data) => (
                <Box
                    key={data.id}
                    sx={{ display: 'flex', flexDirection: 'column', padding: '20px' }}
                >
                    <Stack sx={{ float: 'right', padding: '20px 0' }} direction="row" spacing={2}>
                        <Button
                            color="success"
                            onClick={handleDeliverImages}
                            disabled={shouldDisableDelivery(
                                isDoneUploading,
                                data.thumbnails.length,
                                orderStatus.value,
                                disableDeliveryButton
                            )}
                            variant="contained"
                            startIcon={<CheckCircleOutlineIcon />}
                        >
                            {labels.deliverImagesButton}
                        </Button>
                    </Stack>
                    {data.thumbnails.length > 0 ? (
                        <DigitalStylingThumbnails
                            files={data.thumbnails}
                            filesInProgress={data.uploadsInProgress}
                            onDelete={onDeleteSingleImage}
                            onDeleteMultiple={onDeleteMultipleImages}
                            enableActions={isDoneUploading}
                            onClickDownloadImages={onDownloadImages}
                            labels={labels}
                        />
                    ) : null}
                    {data.editedImagesUploadInProgress ? (
                        <UploadsInProgress
                            labels={{
                                heading: labels.uploadsInProgressHeading
                            }}
                            files={data.uploadsInProgress}
                        />
                    ) : null}
                    {data.failedUploads.length > 0 ? (
                        <FailedUploads
                            labels={{
                                heading: labels.failedUploadsHeading
                            }}
                            files={data.failedUploads}
                            onDelete={onDeleteFailedUpload}
                        />
                    ) : null}
                    <ImageDropzone
                        labels={labels}
                        orderId={orderId}
                        filesAdded={filesAdded}
                        formAction={formAction}
                        addFile={addFileToDropzone}
                        removeFile={removeFileFromDropzone}
                        formId={formId}
                        onUpload={handleUpload}
                        setImageInProgress={setImageInProgress}
                        onRetryUpload={onRetryUpload}
                    />
                    <Button
                        sx={{ alignSelf: 'flex-end' }}
                        variant="contained"
                        type="submit"
                        startIcon={<FileUploadIcon />}
                        disabled={filesAdded.length < 1}
                        form={formId}
                    >
                        {labels.uploadButton}
                    </Button>
                </Box>
            ))}
        </>
    );
}

export default function UploadEditedImagesDataLayer({ orderId, labels }: ExternalProps) {
    let queryKey = 'editedDigitalStylingImages';
    let { data, isError, error } = useQuery([queryKey], () => EditedImagesApi.getImages(orderId), {
        refetchInterval: (current: any) =>
            hasUnsettledImages(current && current.imageData) ? IMAGE_REFETCH_INTERVAL : false
    });

    // TODO: Optimistic UI...

    let queryClient = useQueryClient();

    let deleteFailedUploadMutation = useMutation(
        ({ photoUploadMetadataId }: { photoUploadMetadataId: number }) =>
            EditedImagesApi.deleteFailedUploadImage(photoUploadMetadataId),
        {
            onSettled: () => {
                queryClient.invalidateQueries(queryKey);
            }
        }
    );

    let setImageInProgressMutation = useMutation(
        ({ imageUploadResponse }: { imageUploadResponse: ImageUploadResponse }) =>
            EditedImagesApi.setImageInProgress(imageUploadResponse),
        {
            onSuccess: () => {
                queryClient.invalidateQueries(queryKey);
            }
        }
    );

    let deleteSingleImageMutation = useMutation(
        ({ imageId }: { imageId: number }) => EditedImagesApi.deleteSingleImage(imageId),
        {
            onSettled: () => {
                queryClient.invalidateQueries(queryKey);
            }
        }
    );

    let deleteMultipleImagesMutation = useMutation(
        ({ images }: { images: CustomerOrSupportImage[] }) =>
            EditedImagesApi.deleteMultipleImages(images),
        {
            onSettled: () => {
                queryClient.invalidateQueries(queryKey);
            }
        }
    );

    let deliverImagesMutation = useMutation(() => EditedImagesApi.deliverImages(orderId), {
        onSettled: () => {
            queryClient.invalidateQueries([queryKey, DigitalStylingOrderListQueryKey]);
        }
    });

    function handleDeleteSingleImage(imageId: number): Promise<void> {
        return deleteSingleImageMutation.mutateAsync({ imageId });
    }

    function handleDeleteMultipleImages(images: CustomerOrSupportImage[]): Promise<void> {
        return deleteMultipleImagesMutation.mutateAsync({ images });
    }

    function handleUpload(formData: FormData): Promise<number[]> {
        // We don't need to save metadata for edited images
        let empty: number[] = [];
        return Promise.resolve(empty);
    }

    function handleDeleteFailedUpload(photoUploadMetadataId: number): Promise<void> {
        return deleteFailedUploadMutation.mutateAsync({ photoUploadMetadataId });
    }

    function setImageInProgress(imageUploadResponse: ImageUploadResponse) {
        setImageInProgressMutation.mutate({ imageUploadResponse });
    }

    function handleDownloadImages(): Promise<any> {
        function downloadFunc() {
            return EditedImagesApi.downloadImages(orderId);
        }

        return downloadZip(downloadFunc);
    }

    function handleDeliverImages() {
        return deliverImagesMutation.mutate();
    }

    if (isError) {
        console.error('error fetching image data', error);
    }

    let { orderStatus, imageData } = data ? data : { orderStatus: null, imageData: [] };

    return (
        <UploadEditedImages
            labels={labels}
            orderId={orderId}
            onUpload={handleUpload}
            onDeleteSingleImage={handleDeleteSingleImage}
            onDeleteMultipleImages={handleDeleteMultipleImages}
            onDeleteFailedUpload={handleDeleteFailedUpload}
            imageData={imageData}
            setImageInProgress={setImageInProgress}
            onUploadComplete={() => {}}
            orderStatus={orderStatus}
            onRetryUpload={() => {
                queryClient.invalidateQueries(queryKey);
            }}
            onDeliverImages={handleDeliverImages}
            onDownloadImages={handleDownloadImages}
        />
    );
}
